"use client";

import { useState } from "react";

type Block = { type: string; data: Record<string, any> };
type EditorData = { blocks: Block[] };

const PREVIEW_BLOCKS = 3;

function renderBlock(block: Block, i: number) {
    switch (block.type) {
        case "paragraph":
            return (
                <p key={i} className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: block.data.text }} />
            );
        case "header": {
            const Tag = `h${block.data.level}` as keyof JSX.IntrinsicElements;
            return <Tag key={i} className="font-bold text-blackColor mt-4">{block.data.text}</Tag>;
        }
        case "list": {
            const renderItem = (item: any, j: number) => {
                const content = typeof item === "string" ? item : item.content ?? "";
                return <li key={j} dangerouslySetInnerHTML={{ __html: content }} />;
            };
            return block.data.style === "ordered" ? (
                <ol key={i} className="list-decimal pl-5 space-y-1 text-gray-700">
                    {block.data.items.map(renderItem)}
                </ol>
            ) : (
                <ul key={i} className="list-disc pl-5 space-y-1 text-gray-700">
                    {block.data.items.map(renderItem)}
                </ul>
            );
        }
        case "image":
            return (
                <div key={i} className="my-4">
                    <img src={block.data.file?.url} alt={block.data.caption || ""} className="rounded-lg max-w-full" />
                    {block.data.caption && (
                        <p className="text-xs text-gray-400 mt-1 text-center">{block.data.caption}</p>
                    )}
                </div>
            );
        default:
            return null;
    }
}

export default function ProductDescription({ data }: { data: EditorData }) {

    const [expanded, setExpanded] = useState(false);

    const visibleBlocks = expanded ? data.blocks : data.blocks.slice(0, PREVIEW_BLOCKS);
    const hasMore = data.blocks.length > PREVIEW_BLOCKS;

    return (
        <div className="flex flex-col w-full space-y-4">
            <div className="relative">
                <div className="flex flex-col space-y-4">
                    {visibleBlocks.map((block, i) => renderBlock(block, i))}
                </div>

                {/* Fade overlay when collapsed */}
                {!expanded && hasMore && (
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-whiteColor to-transparent pointer-events-none" />
                )}
            </div>

            {hasMore && (
                <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="self-start text-sm font-medium text-redColor hover:underline transition-colors"
                >
                    {expanded ? "Read less ↑" : "Read more ↓"}
                </button>
            )}
        </div>
    );
}